/**
 * KEY-MADE — Super-Admin Yard-Graph Diagnostic + Repair (iter373).
 *
 * Rendered under Admin Settings → Diagnostics.  Talks to whichever
 * database the running backend is bound to — preview when local,
 * PRODUCTION when the app is deployed.  This is the ONE tool that
 * lets the user (Chad) repair his tangled production graph WITHOUT
 * requiring Emergent to reach into his DB.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, RefreshCw, Layers, GitMerge, Trash2, Link2, Wrench } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

async function callApi(method, path, body) {
  const jwt = localStorage.getItem('accessToken') || '';
  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};
  const url = `${API}${path}`;
  if (method === 'GET') return (await axios.get(url, { headers })).data;
  return (await axios[method.toLowerCase()](url, body || {}, { headers })).data;
}

function TypedConfirm({ expected, onConfirm, testId }) {
  const [v, setV] = useState('');
  return (
    <div className="flex items-center gap-2">
      <Input
        value={v}
        placeholder={`Type: ${expected}`}
        onChange={(e) => setV(e.target.value)}
        className="text-xs"
        data-testid={`${testId}-input`}
      />
      <Button
        size="sm"
        variant="destructive"
        disabled={v !== expected}
        onClick={() => { onConfirm(v); setV(''); }}
        data-testid={`${testId}-btn`}
      >
        Confirm
      </Button>
    </div>
  );
}

export default function AdminYardGraphPanel({ darkMode, cardClass, textClass }) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // DP76 — a refresh after a repair must NOT unmount the panel: the early
  // `if (loading)` return threw away the result the user had just asked for.
  const reload = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const d = await callApi('GET', '/admin/yard-graph');
      setGraph(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const merge = async (source_id, target_id, confirm) => {
    setBusy(true);
    try {
      const r = await callApi('POST', '/admin/yard-graph/merge', { source_id, target_id, confirm });
      toast.success(`Merged. Snapshot ${r.snapshot_id?.slice(0, 8)}… Reversible via /restore/{id}`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); }
    finally { setBusy(false); }
  };

  const repoint = async (host_wt_id, tile_item_id, new_board_id, confirm) => {
    setBusy(true);
    try {
      const r = await callApi('POST', '/admin/yard-graph/repoint', {
        host_wt_id, tile_item_id, new_board_id, confirm,
      });
      toast.success(`Re-pointed. Snapshot ${r.snapshot_id?.slice(0, 8)}…`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Repoint failed'); }
    finally { setBusy(false); }
  };

  const archive = async (wt_id, reason, confirm) => {
    setBusy(true);
    try {
      const r = await callApi('POST', '/admin/yard-graph/archive', { wt_id, reason, confirm });
      toast.success(`Archived. Snapshot ${r.snapshot_id?.slice(0, 8)}…`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className={textClass}>Loading Yard graph…</div>;
  if (!graph) return <div className={textClass}>No data.</div>;

  const dupHomes = graph.duplicate_homes || [];
  const dupCanvases = graph.duplicate_canvases || [];
  const misfires = graph.misfired_tiles || [];
  const homeNodes = (graph.nodes || []).filter(n => n.is_home);

  return (
    <div className="space-y-4" data-testid="admin-yard-graph">
      <div className="flex items-center justify-between">
        <div>
          <div className={`text-lg font-semibold ${textClass || ''}`}>Yard Graph Diagnostic</div>
          <div className={darkMode ? 'text-xs text-gray-400' : 'text-xs text-muted-foreground'}>
            {graph.total_wts} work-tables · iter {graph.iter} · runs against the LIVE DB the backend is bound to.
          </div>
        </div>
        <Button size="sm" onClick={reload} variant="outline" data-testid="yard-graph-reload">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reload
        </Button>
      </div>

      {/* Duplicate homes */}
      <Card className={`${cardClass || ''}`} data-testid="yard-graph-duplicate-homes">
        <CardHeader>
          <CardTitle className={`text-sm flex items-center gap-2 ${textClass}`}>
            <AlertTriangle className="w-4 h-4 text-orange-500" /> Duplicate Home Boards ({dupHomes.length})
          </CardTitle>
          <CardDescription className={darkMode ? 'text-gray-400' : ''}>
            More than one <code>is_home:true</code> doc for the same user in this tenant.  Archive the empty/older ones.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {dupHomes.length === 0 && <div className={darkMode ? 'text-xs text-gray-400' : 'text-xs text-muted-foreground'}>None. ✓</div>}
          {dupHomes.map((g, gi) => (
            <div key={gi} className={`p-2 rounded border ${darkMode ? 'border-[#2A2A2A]' : 'border-border'}`}>
              <div className={`text-xs mb-2 ${textClass}`}>user_id: <code>{g.user_id}</code></div>
              {g.docs.map(d => (
                <div key={d.id} className="flex items-center justify-between gap-2 py-1 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className={textClass}><b>{d.name}</b> — <code className="text-[10px]">{d.id}</code></div>
                    <div className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>
                      {d.item_count} items · {d.board_tile_count} block tiles · updated {d.updated_at || '?'}
                    </div>
                  </div>
                  <TypedConfirm
                    expected={`ARCHIVE ${d.id}`}
                    onConfirm={(c) => archive(d.id, 'duplicate_home', c)}
                    testId={`archive-${d.id.slice(0, 6)}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Duplicate canvases (diverged Yards holding same-named Blocks) */}
      <Card className={`${cardClass || ''}`} data-testid="yard-graph-duplicate-canvases">
        <CardHeader>
          <CardTitle className={`text-sm flex items-center gap-2 ${textClass}`}>
            <Layers className="w-4 h-4 text-orange-500" /> Diverged Canvases ({dupCanvases.length})
          </CardTitle>
          <CardDescription className={darkMode ? 'text-gray-400' : ''}>
            Two home canvases holding ≥3 same-named Blocks — the tangle Chad reported.  Merge the poorer INTO the richer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {dupCanvases.length === 0 && <div className={darkMode ? 'text-xs text-gray-400' : 'text-xs text-muted-foreground'}>None. ✓</div>}
          {dupCanvases.map((c, ci) => {
            const rich = c.a_count >= c.b_count ? c : { ...c, a_id: c.b_id, a_name: c.b_name, a_count: c.b_count, b_id: c.a_id, b_name: c.a_name, b_count: c.a_count };
            return (
              <div key={ci} className={`p-2 rounded border ${darkMode ? 'border-[#2A2A2A]' : 'border-border'} text-xs`}>
                <div className={textClass}>
                  <b>{rich.a_name}</b> ({rich.a_count} items) vs <b>{rich.b_name}</b> ({rich.b_count} items).
                </div>
                <div className={darkMode ? 'text-gray-400 mt-1' : 'text-muted-foreground mt-1'}>
                  Shared: {rich.shared_titles.join(', ')}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={darkMode ? 'text-gray-300' : 'text-slate-700'}>
                    <GitMerge className="w-3 h-3 inline mr-1" />
                    Merge <code>{rich.b_id.slice(0, 8)}</code> INTO <code>{rich.a_id.slice(0, 8)}</code>:
                  </span>
                  <TypedConfirm
                    expected={`MERGE ${rich.b_id}`}
                    onConfirm={(cf) => merge(rich.b_id, rich.a_id, cf)}
                    testId={`merge-${rich.b_id.slice(0, 6)}`}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Misfired tiles */}
      <Card className={`${cardClass || ''}`} data-testid="yard-graph-misfires">
        <CardHeader>
          <CardTitle className={`text-sm flex items-center gap-2 ${textClass}`}>
            <Link2 className="w-4 h-4 text-orange-500" /> Mis-wired Block Tiles ({misfires.length})
          </CardTitle>
          <CardDescription className={darkMode ? 'text-gray-400' : ''}>
            A Block tile whose <code>board_id</code> points at a HOME/root doc — clicking it opens a second home.  Re-point to its intended child WT.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {misfires.length === 0 && <div className={darkMode ? 'text-xs text-gray-400' : 'text-xs text-muted-foreground'}>None. ✓</div>}
          {misfires.map((m, mi) => (
            <div key={mi} className={`p-2 rounded border ${darkMode ? 'border-[#2A2A2A]' : 'border-border'} text-xs`}>
              <div className={textClass}>
                <b>{m.tile_title || 'Untitled'}</b> on host <b>{m.host_wt_name}</b>
              </div>
              <div className={darkMode ? 'text-gray-400 mt-1' : 'text-muted-foreground mt-1'}>
                Currently points at HOME <code>{m.board_id}</code>. Enter the CORRECT child WT id below and confirm to repoint.
              </div>
              <RepointRow
                host_wt_id={m.host_wt_id}
                tile_item_id={m.tile_item_id}
                current_board_id={m.board_id}
                onRepoint={repoint}
                darkMode={darkMode}
                nodes={homeNodes}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Home summary */}
      <Card className={`${cardClass || ''}`} data-testid="yard-graph-homes-summary">
        <CardHeader>
          <CardTitle className={`text-sm ${textClass}`}>Home Boards In This Tenant ({homeNodes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-xs">
            {homeNodes.map(h => (
              <div key={h.id} className={`flex items-center justify-between py-1 border-b ${darkMode ? 'border-[#2A2A2A]' : 'border-border'}`}>
                <div className={textClass}>
                  <b>{h.name}</b> · <code className="text-[10px]">{h.id}</code>
                </div>
                <div className={darkMode ? 'text-gray-400' : 'text-muted-foreground'}>
                  {h.item_count} items · {h.board_tile_count} block tiles · user <code className="text-[10px]">{h.user_id?.slice(0, 8)}</code>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* DP76 — the three repairs Chad runs himself, beside the read-only
          diagnostic. Every one snapshots first and reports what it changed. */}
      <YardRepairsCard darkMode={darkMode} cardClass={cardClass} textClass={textClass}
                      nodes={graph.nodes || []} busy={busy} setBusy={setBusy} reload={reload} />
    </div>
  );
}

function YardRepairsCard({ darkMode, cardClass, textClass, nodes, busy, setBusy, reload }) {
  const [yardId, setYardId] = useState('');
  const [plan, setPlan] = useState(null);
  const [orphanPlan, setOrphanPlan] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const muted = darkMode ? 'text-xs text-gray-400' : 'text-xs text-muted-foreground';

  const run = async (label, path, body, after) => {
    setBusy(true);
    try {
      const r = await callApi('POST', path, body);
      setLastResult({ label, r });
      if (after) after(r);
      toast.success(`${label}: done`);
      await reload(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
      setLastResult({ label, error: e?.response?.data?.detail || 'failed' });
    } finally { setBusy(false); }
  };

  return (
    <Card className={cardClass || ''} data-testid="yard-repairs-card">
      <CardHeader>
        <CardTitle className={`text-sm flex items-center gap-2 ${textClass}`}>
          <Wrench className="w-4 h-4" /> Yard repairs
        </CardTitle>
        <CardDescription className={darkMode ? 'text-gray-400' : ''}>
          Runs against the database this backend is bound to. Each one snapshots first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* 1 — set this board as my Yard */}
        <div>
          <div className={`text-xs font-semibold mb-1 ${textClass}`}>Set this board as my Yard</div>
          <div className={`${muted} mb-2`}>
            Clears the old <code>is_home</code> first, then sets it — the unique index refuses two.
            Refused if the board holds no items.
          </div>
          <select
            value={yardId}
            onChange={(e) => setYardId(e.target.value)}
            className="text-xs border rounded px-2 py-1 w-full mb-2 bg-transparent"
            data-testid="set-yard-select"
          >
            <option value="">Choose a board…</option>
            {[...nodes]
              .filter((n) => !n.is_home)
              .sort((a, b) => (b.item_count || 0) - (a.item_count || 0))
              .slice(0, 40)
              .map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name} — {n.item_count || 0} items
                </option>
              ))}
          </select>
          {yardId && (
            <TypedConfirm
              expected={`SET YARD ${yardId}`}
              onConfirm={(c) => run('Set Yard', '/admin/yard-graph/set-yard', { wt_id: yardId, confirm: c })}
              testId="set-yard"
            />
          )}
        </div>

        {/* 2 — backfill parents */}
        <div>
          <div className={`text-xs font-semibold mb-1 ${textClass}`}>Backfill child-board parents</div>
          <div className={`${muted} mb-2`}>
            Gives a child board the parent that tiles it, so breadcrumbs climb. Dry run first.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run('Backfill dry run', '/admin/yard-graph/backfill-parents',
                                       { dry_run: true }, (r) => setPlan(r))}
                    data-testid="backfill-dry-run-btn">
              Dry run
            </Button>
            {plan && (
              <span className={muted} data-testid="backfill-plan-summary">
                {plan.changes?.length || 0} to change · {plan.already_correct} already right ·
                {' '}{plan.skipped_missing_target} skipped (no board) · {plan.conflicts?.length || 0} conflicts
              </span>
            )}
          </div>
          {plan?.changes?.length > 0 && (
            <div className="mt-2 space-y-1">
              {plan.changes.map((c) => (
                <div key={c.child_id} className={muted} data-testid={`backfill-row-${c.child_id}`}>
                  <code className="text-[10px]">{c.child_id.slice(0, 8)}</code> {c.child_name} —
                  {' '}parent <b>null</b> → <b>{c.parent_name}</b>
                </div>
              ))}
              <TypedConfirm
                expected={`BACKFILL ${plan.changes.length}`}
                onConfirm={(c) => run('Backfill', '/admin/yard-graph/backfill-parents',
                                      { dry_run: false, confirm: c }, () => setPlan(null))}
                testId="backfill-apply"
              />
            </div>
          )}
        </div>

        {/* 3 — clear dead Block links */}
        <div>
          <div className={`text-xs font-semibold mb-1 ${textClass}`}>Clear dead Block links</div>
          <div className={`${muted} mb-2`}>
            A Block pointing at a board that does not exist stops offering to open one.
            The Block itself is kept and no board is created.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run('Orphan dry run', '/admin/yard-graph/clear-orphan-tiles',
                                       { dry_run: true }, (r) => setOrphanPlan(r))}
                    data-testid="orphans-dry-run-btn">
              Dry run
            </Button>
            {orphanPlan && (
              <span className={muted} data-testid="orphans-plan-summary">
                {orphanPlan.orphans?.length || 0} dead link(s) across {orphanPlan.boards_scanned} boards
              </span>
            )}
          </div>
          {orphanPlan?.orphans?.length > 0 && (
            <div className="mt-2 space-y-1">
              {orphanPlan.orphans.map((o) => (
                <div key={o.item_id} className={muted} data-testid={`orphan-row-${o.item_id}`}>
                  <b>{o.title}</b> on {o.host_name} → dead{' '}
                  <code className="text-[10px]">{o.dead_board_id?.slice(0, 8)}</code>
                  {' '}· remembered {o.remembered_item_count ?? 0} items · created {o.item_created_at || 'unknown'}
                </div>
              ))}
              <TypedConfirm
                expected={`CLEAR ${orphanPlan.orphans.length}`}
                onConfirm={(c) => run('Clear dead links', '/admin/yard-graph/clear-orphan-tiles',
                                      { dry_run: false, confirm: c }, () => setOrphanPlan(null))}
                testId="orphans-apply"
              />
            </div>
          )}
        </div>

        {lastResult && (
          <pre className="text-[10px] overflow-auto max-h-40 p-2 rounded border" data-testid="yard-repairs-result">
            {JSON.stringify(lastResult, null, 1)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

function RepointRow({ host_wt_id, tile_item_id, current_board_id, onRepoint, darkMode }) {
  const [newBid, setNewBid] = useState('');
  return (
    <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
      <Input
        value={newBid}
        onChange={(e) => setNewBid(e.target.value)}
        placeholder="new child WT id"
        className="text-xs"
        data-testid={`repoint-${tile_item_id.slice(0, 8)}-target`}
      />
      <TypedConfirm
        expected={`REPOINT ${tile_item_id}`}
        onConfirm={(c) => newBid && onRepoint(host_wt_id, tile_item_id, newBid, c)}
        testId={`repoint-${tile_item_id.slice(0, 6)}`}
      />
    </div>
  );
}
